import { Injectable } from '@angular/core';

export interface ChurchThemePreset {
  id: string;
  name: string;
  primary: string;
  submenu: string;
  accent: string;
  page: string;
  surface: string;
  text: string;
  success: string;
  danger: string;
  warning: string;
  info: string;
}

export const CHURCH_THEME_PRESETS: ChurchThemePreset[] = [
  { id: 'royal', name: 'Royal Purple', primary: '#513b8a', submenu: '#3d2c70', accent: '#e7e0f5', page: '#f5f2fa', surface: '#ffffff', text: '#302940', success: '#16706e', danger: '#845125', warning: '#936000', info: '#276a89' },
  { id: 'ocean', name: 'Ocean Blue', primary: '#005dbc', submenu: '#074b91', accent: '#d7e9fb', page: '#f1f6fb', surface: '#ffffff', text: '#263746', success: '#087e8b', danger: '#713a91', warning: '#a66700', info: '#16708a' },
  { id: 'emerald-green', name: 'Emerald Green', primary: '#087a5b', submenu: '#075b46', accent: '#d9f1e8', page: '#f0f7f4', surface: '#ffffff', text: '#23382f', success: '#245d9c', danger: '#704a9e', warning: '#946200', info: '#176f80' },
  { id: 'burgundy', name: 'Burgundy', primary: '#872d49', submenu: '#6e2037', accent: '#f3e1e7', page: '#f8f2f4', surface: '#ffffff', text: '#392831', success: '#0b777d', danger: '#875218', warning: '#9a5d00', info: '#236988' },
  { id: 'warm-orange', name: 'Warm Orange', primary: '#b6531b', submenu: '#8a3f16', accent: '#fbe6d7', page: '#fbf4ee', surface: '#ffffff', text: '#3d2a20', success: '#245d82', danger: '#813f69', warning: '#925a00', info: '#176c83' },
  { id: 'modern-gray', name: 'Modern Gray', primary: '#4b5563', submenu: '#374151', accent: '#e5e7eb', page: '#f3f4f6', surface: '#ffffff', text: '#29313b', success: '#245d9c', danger: '#704a9e', warning: '#835b15', info: '#176f80' },
  { id: 'teal', name: 'Teal', primary: '#087e8b', submenu: '#06616d', accent: '#d8f0f1', page: '#eff7f7', surface: '#ffffff', text: '#243638', success: '#62458d', danger: '#8a4f21', warning: '#936200', info: '#205c91' },
  { id: 'sage', name: 'Sage', primary: '#607c62', submenu: '#4c654e', accent: '#e5eee3', page: '#f4f7f2', surface: '#ffffff', text: '#303a30', success: '#216c75', danger: '#814b70', warning: '#8a5d18', info: '#315f82' },
  { id: 'gold', name: 'Gold', primary: '#8a681a', submenu: '#6e5113', accent: '#f5eacb', page: '#faf7ee', surface: '#ffffff', text: '#3d3523', success: '#176f72', danger: '#784765', warning: '#835814', info: '#315e80' },
  { id: 'terracotta', name: 'Terracotta', primary: '#a84f3b', submenu: '#803b2e', accent: '#f5e1da', page: '#faf2ef', surface: '#ffffff', text: '#3d2b28', success: '#206f79', danger: '#744777', warning: '#895a16', info: '#2b6680' },
  { id: 'navy-aqua', name: 'Navy & Aqua', primary: '#153c5a', submenu: '#102f49', accent: '#d6edf1', page: '#eff5f7', surface: '#ffffff', text: '#243541', success: '#14726f', danger: '#765080', warning: '#8d641a', info: '#14677d' },
  { id: 'rose-mauve', name: 'Rose & Mauve', primary: '#95556e', submenu: '#754157', accent: '#f3e3ea', page: '#faf3f6', surface: '#ffffff', text: '#3e2b34', success: '#1c7472', danger: '#87502a', warning: '#956313', info: '#316783' }
];

@Injectable({ providedIn: 'root' })
export class ChurchThemeService {
  constructor() {
    if (typeof window === 'undefined') return;
    const cachedTheme = this.getCachedThemeId();
    if (cachedTheme) this.applyTheme(cachedTheme);
  }

  applyTheme(themeId: string | null | undefined, tenantId?: string, cacheForTenant = false): void {
    const compatibleThemeId = themeId === 'forest' ? 'emerald-green'
      : themeId === 'sunrise' ? 'warm-orange'
        : themeId;
    const theme = CHURCH_THEME_PRESETS.find(preset => preset.id === compatibleThemeId)
      ?? CHURCH_THEME_PRESETS.find(preset => preset.id === 'ocean')
      ?? CHURCH_THEME_PRESETS[0];
    const root = document.documentElement;

    root.style.setProperty('--church-primary', theme.primary);
    root.style.setProperty('--church-loader-color', theme.primary);
    root.style.setProperty('--church-primary-hover', this.adjustBrightness(theme.primary, -0.12));
    root.style.setProperty('--church-submenu', theme.submenu);
    root.style.setProperty('--church-accent', theme.accent);
    root.style.setProperty('--church-page-background', theme.page);
    root.style.setProperty('--church-surface', theme.surface);
    root.style.setProperty('--church-text', theme.text);
    root.style.setProperty('--church-muted', this.mix(theme.text, theme.surface, 0.62));
    root.style.setProperty('--church-border', this.mix(theme.text, theme.surface, 0.2));
    root.style.setProperty('--church-highlight', theme.accent);

    root.style.setProperty('--church-success', theme.success);
    root.style.setProperty('--church-danger', theme.danger);
    root.style.setProperty('--church-warning', theme.warning);
    root.style.setProperty('--church-info', theme.info);

    root.style.setProperty('--church-primary-foreground', this.contrastColor(theme.primary));
    root.style.setProperty('--church-submenu-foreground', this.contrastColor(theme.submenu));
    root.style.setProperty('--church-accent-foreground', this.contrastColor(theme.accent));
    root.style.setProperty('--church-success-foreground', this.contrastColor(theme.success));
    root.style.setProperty('--church-danger-foreground', this.contrastColor(theme.danger));
    root.style.setProperty('--church-warning-foreground', this.contrastColor(theme.warning));
    root.style.setProperty('--church-info-foreground', this.contrastColor(theme.info));

    if (cacheForTenant && tenantId && typeof localStorage !== 'undefined') {
      localStorage.setItem(this.getCacheKey(tenantId), theme.id);
    }
  }

  private getCachedThemeId(): string | null {
    const pathTenant = window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase();
    const nonTenantRoutes = new Set(['home', 'login', 'signup', 'resetpassword', '404', 'admin', 'member', 'usher']);
    const host = window.location.hostname.toLowerCase();
    let tenantId: string | null = null;

    if (pathTenant && !nonTenantRoutes.has(pathTenant)) {
      tenantId = pathTenant;
    } else if (host.endsWith('.localhost')) {
      tenantId = host.slice(0, -'.localhost'.length).split('.').pop() ?? null;
    } else {
      const knownDomain = ['church-services.org', 'church-services-staging.org']
        .find(domain => host.endsWith(`.${domain}`));
      if (knownDomain) tenantId = host.slice(0, -(knownDomain.length + 1)).split('.').pop() ?? null;
    }

    tenantId = tenantId || localStorage.getItem('tenant')?.toLowerCase() || 'main';
    const themeId = localStorage.getItem(this.getCacheKey(tenantId));
    return CHURCH_THEME_PRESETS.some(theme => theme.id === themeId)
      || themeId === 'forest'
      || themeId === 'sunrise'
      ? themeId
      : null;
  }

  private getCacheKey(tenantId: string): string {
    return `church.colorTheme.${tenantId.toLowerCase()}`;
  }

  private contrastColor(hex: string): string {
    const rgb = this.toRgb(hex).map(channel => {
      const normalized = channel / 255;
      return normalized <= 0.04045 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
    });
    const luminance = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
    const whiteContrast = 1.05 / (luminance + 0.05);
    const darkContrast = (luminance + 0.05) / 0.068;
    return whiteContrast >= darkContrast ? '#ffffff' : '#172535';
  }

  private adjustBrightness(hex: string, amount: number): string {
    return `#${this.toRgb(hex).map(channel => Math.max(0, Math.min(255, Math.round(channel * (1 + amount)))).toString(16).padStart(2, '0')).join('')}`;
  }

  private mix(foreground: string, background: string, foregroundWeight: number): string {
    const foregroundRgb = this.toRgb(foreground);
    const backgroundRgb = this.toRgb(background);
    return `#${foregroundRgb.map((channel, index) => Math.round(channel * foregroundWeight + backgroundRgb[index] * (1 - foregroundWeight)).toString(16).padStart(2, '0')).join('')}`;
  }

  private toRgb(hex: string): number[] {
    const normalized = hex.replace('#', '');
    return [0, 2, 4].map(index => parseInt(normalized.slice(index, index + 2), 16));
  }
}
